import { errorMessage, isCancellation } from "@streamoid/errors";
import axios from "axios";
import async from "async";

import DSOS from "../Icons/DS-OS.svg";
import PSOS from "../Icons/PS-OS.svg";
import CSOS from "../Icons/CS-OS.svg";

import centralOSIcon from "../Icons/loginPage/central-os-logo.svg";
import catalogStudioIcon from "../Icons/loginPage/catalog-studio-logo.svg";
import photoStudioIcon from "../Icons/loginPage/photo-studio-logo.svg";
import designStudioIcon from "../Icons/loginPage/design-studio-logo.svg";

import { toast } from "react-toastify";
export const SUPPORTED_FILE_TYPES = ["image/png", "image/jpg", "image/jpeg"];
export const MAX_IMG_FILE_SIZE = 5 * 1024 * 1024;
import Compressor from "compressorjs";

import { PHOTOGENIX_API, PHOTOGENIX_API_ADMIN_TOKEN } from "./envConfig";

export const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
};

export function deleteCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

export const getQueryParamsAsObj = () => {
  const { search } = window.location;
  const queryParams = search
    ? JSON.parse(
        '{"' +
          decodeURI(search)
            .replace("?", "")
            .replace(/"/g, '\\"')
            .replace(/&/g, '","')
            .replace(/=/g, '":"') +
          '"}'
      )
    : {};
  return queryParams;
};

export const getInitials = (name) => {
  if (name) {
    return name.substring(0, 1);
  }

  return name;
};
export const WEBSITE_REGEX =
  /^(https?:\/\/)([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})(:[0-9]+)?(\/\S*)?$/;
export const EMAIL_REGEX = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,4})+$/;
const getDateSuffix = function (date) {
  if (date > 3 && date < 21) return "th";
  switch (date % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

export const formatDateToHumanReadable = (
  jsDate,
  showTime = false,
  showMilliseconds = false
) => {
  const fortnightAway = new Date(jsDate);

  const date = fortnightAway.getDate();
  const month = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ][fortnightAway.getMonth()];

  let time = false;

  if (showTime) {
    const hours = fortnightAway.getHours();
    const formattedHour = hours % 12 || 12;
    const minutes = String(fortnightAway.getMinutes()).padStart(2, "0");
    const seconds = String(fortnightAway.getSeconds()).padStart(2, "0");
    const milliseconds = String(fortnightAway.getMilliseconds()).padStart(
      3,
      "0"
    );
    const meridiem = hours >= 12 ? "PM" : "AM";

    time = showMilliseconds
      ? `${formattedHour}:${minutes}:${seconds}.${milliseconds} ${meridiem}`
      : `${formattedHour}:${minutes} ${meridiem}`;
  }

  const fullDate = `${month} ${date}${getDateSuffix(date)}, ${fortnightAway
    .getFullYear()
    .toString()}`;
  return showTime ? `${fullDate} - ${time}` : fullDate;
};

export const generateRandomArrayWithSum = (targetSum, length) => {
  const array = [];
  let remaining = targetSum;

  for (let i = 0; i < length - 1; i++) {
    const value =
      Math.floor(Math.random() * (remaining - (length - i - 1))) + 1;
    array.push(value);
    remaining -= value;
  }

  array.push(remaining);
  return array.sort(() => Math.random() - 0.5);
};

// Icon mapping object
export const groupServiceIconMap = {
  "DS-OS": DSOS,
  "PS-OS": PSOS,
  "CS-OS": CSOS,
};

export const loginPageBrandLogoAndDesc = {
  "central-os": {
    icon: centralOSIcon,
    heading: "The AI executive driving your core brand operations",
    description:
      "Automate product creation, content workflows, and channel operations with intelligent agents.",
  },
  "catalog-studio": {
    icon: catalogStudioIcon,
    heading: "The AI Powered Catalog Studio",
    description:
      "Organize all your product data in our PIM. AI agents generate complete product attribution and SEO content and help list on any channel in minutes.",
  },
  "photo-studio": {
    icon: photoStudioIcon,
    heading: "The AI Powered Photo Studio",
    description:
      "Streamline your studio operations with AI, purpose built for fashion and lifestyle shoots. ",
  },
  "design-studio": {
    icon: designStudioIcon,
    heading: "The AI Powered Design Studio",

    description:
      "AI agents streamline design research, moodboard creation, style development, and techpack generation.",
  },
};

export const notify = (
  message = "",
  type = "success",
  timeout,
  showCloseBtn = true
) => {
  if (isCancellation(message)) return;
  const displayMessage = typeof message === "string" ? message : errorMessage(message);
  const isInfinite = timeout === "infinite" || (type === "error" && timeout == null);

  const toastProps = {
    position: "bottom-center",
    autoClose: isInfinite ? false : timeout || 3000,
    closeOnClick: !isInfinite,
    draggable: false,
    pauseOnHover: false,
    pauseOnFocusLoss: false,
    hideProgressBar: true,
    closeButton: showCloseBtn,
    toastClassName: "custom-toast",
  };

  const toastTypes = {
    info: toast.info,
    warning: toast.warning,
    error: toast.error,
    success: toast.success,
  };

  const showToast = toastTypes[type] || toast.success;
  showToast(displayMessage, toastProps);
};

const compressFile = (file, maxFileSize) => {
  return new Promise((resolve, reject) => {
    new Compressor(file, {
      quality: 0.6,
      success(result) {
        if (result.size > maxFileSize) {
          reject(
            `file size exceeded accepted limit (${
              maxFileSize / (1024 * 1024)
            } MB)`
          );
        } else {
          resolve(result);
        }
      },
      error(err) {
        console.log(err.message);
        if (file.size > maxFileSize) {
          reject(
            `file size exceeded accepted limit (${
              maxFileSize / (1024 * 1024)
            } MB)`
          );
        } else {
          resolve(file);
        }
      },
    });
  });
};

export const processFile = (
  file,
  maxFileSize = MAX_IMG_FILE_SIZE,
  supportedFileTypes = SUPPORTED_FILE_TYPES
) => {
  return new Promise((resolve, reject) => {
    if (!supportedFileTypes.includes(file.type)) {
      reject("Unsupported file type");
    } else {
      compressFile(file, maxFileSize)
        .then((res) => {
          const file = new File([res], res.name);
          resolve(file);
        })
        .catch((err) => {
          reject(err);
        });
    }
  });
};

export const getNextMonthDate = (input) => {
  const date = new Date(input);

  // Add one month
  date.setMonth(date.getMonth() + 1);

  return date.toISOString();
};

export const getNextYearDate = (input) => {
  const date = new Date(input);

  // Add one year
  date.setFullYear(date.getFullYear() + 1);

  return date.toISOString();
};

export const generateUniqueId = (length = 10) => {
  let result = "";
  for (let i = 0; i < length; i++) {
    result += Math.floor(Math.random() * 10);
  }
  return result;
};

async function uploadPart(
  selectedZip,
  PART_SIZE,
  url,
  uploadId,
  index,
  totalParts
) {
  const start = PART_SIZE * index;
  const end = Math.min(start + PART_SIZE, selectedZip.size);
  const part = selectedZip.slice(start, end);

  const retries = 3;
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const response = await axios.put(url, part, {
        params: {
          action: "mpu-uploadpart",
          uploadId,
          partNumber: String(index + 1),
        },
        headers: {
          "Content-Type": "application/octet-stream",
        },
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
      });

      return response.data;
    } catch (error) {
      console.log(error);
      console.log(`Retry attempt ${attempt + 1} for part ${index + 1} failed`);
      if (attempt === retries - 1) {
        throw error;
      }
    }
  }
}

export function uploadFileToCloudfare(selectedZip, handleUploadProgress) {
  let UPLOADED_PARTS_COUNT = 0;

  return new Promise(async (resolve, reject) => {
    try {
      const PART_SIZE = 9 * 1024 * 1024;
      const WORKER_ENDPOINT = "https://file-upload-multi.catalogix.ai/";

      const url = `${WORKER_ENDPOINT}${Date.now()}_${selectedZip.name.replace(
        /\s+/g,
        ""
      )}`;
      console.log("main URL: ", url);
      const response = await axios.post(url, null, {
        params: { action: "mpu-create" },
      });
      const uploadId = response && response.data && response.data.uploadId;

      const partCount = Math.ceil(selectedZip.size / PART_SIZE);
      console.log("Total parts for zip upload:", partCount);

      const tasks = Array.from({ length: partCount }, (_, index) => {
        return (cb) => {
          console.log(`Uploading part ${index + 1}`);
          uploadPart(selectedZip, PART_SIZE, url, uploadId, index, partCount)
            .then((res) => {
              console.log(`Part ${index + 1} uploaded successfully`);
              UPLOADED_PARTS_COUNT += 1;
              if (handleUploadProgress)
                handleUploadProgress(
                  parseInt((UPLOADED_PARTS_COUNT / partCount) * 100)
                );
              cb(null, res);
            })
            .catch((error) => {
              console.log(`Failed to upload part ${index + 1}`);
              console.log(`${index + 1}: ${error}`);
              cb(error, null);
            });
        };
      });

      async.parallelLimit(tasks, 10, async (err, uploadedParts) => {
        if (err) {
          console.error("Error uploading parts", err);
        }

        const finalUrl = url + "?action=mpu-complete&uploadId=" + uploadId;
        try {
          const completeResponse = await axios.post(finalUrl, {
            parts: uploadedParts,
          });

          if (completeResponse.status === 200) {
            resolve(url + "?action=get");
          } else {
            console.error("ERROR:", completeResponse.data);
            reject(new Error("Failed to complete multipart upload"));
          }
        } catch (completeError) {
          console.error("Error completing multipart upload", completeError);
          reject(completeError);
        }
      });
    } catch (error) {
      console.log("Error in uploadFileToCloudfare:", error);
      reject(error);
    }
  });
}

export const uploadImages = async (imageFiles) => {
  try {
    const formData = new FormData();
    imageFiles.forEach((file) => {
      formData.append("image", file);
    });
    const response = await axios.post(
      PHOTOGENIX_API + "/api/image-upload",
      formData
    );
    return response;
  } catch (err) {
    return err;
  }
};

// export const uploadImages = async (file) => {
//   console.log("@uploadImageToS3Async");

//   if (!file || typeof file !== "object") {
//     const errorResponse = { message: "Invalid file object", statusCode: 400 };
//     if (cb) return cb(errorResponse);
//     throw errorResponse;
//   }

//   const mimeType = file.type || file.name;
//   const bytes = await file.arrayBuffer();

//   if (!bytes) {
//     const errorResponse = {
//       message: "File data is undefined",
//       statusCode: 400,
//     };
//     if (cb) return cb(errorResponse);
//     throw errorResponse;
//   }

//   const formData = new FormData();
//   formData.append(
//     "image_data",
//     new Blob([bytes], { type: mimeType }),
//     file.name
//   );

//   try {
//     const response = await fetch(`${IMAGE_UPLOAD_API}`, {
//       method: "POST",
//       body: formData,
//       headers: {},
//     });

//     if (!response.ok) {
//       const errorResponse = {
//         message: "Upload failed",
//         statusCode: response.status,
//       };
//       if (cb) return cb(errorResponse);
//       throw errorResponse;
//     }

//     const body = await response.json();
//     console.log(body);

//     if (body.status && body.status.code === 0) {
//       if (cb) return cb(null, body);
//       return body;
//     } else {
//       const errorResponse = { body, statusCode: 500 };
//       if (cb) return cb(errorResponse);
//       throw errorResponse;
//     }
//   } catch (error) {
//     console.error("Upload Error:", error);
//     if (cb) return cb(error);
//     throw error;
//   }
// };
